import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Activity,
  BookOpen,
  BrainCircuit,
  Cloud,
  CloudOff,
  Download,
  Home,
  LogOut,
  MessageCircle,
  RefreshCw,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import {
  COMPANION_APP_PAGES,
  getCompanionPageFromPath,
} from "@/components/daily/companionAppKnowledge";
import { useCloudAccount } from "@/components/account/CloudAccountGate";
import { getAdminSession } from "@/components/admin/adminApi";

const ICONS = {
  home: Home,
  checkIn: Activity,
  plan: BrainCircuit,
  learn: BookOpen,
  companion: MessageCircle,
};

const BOTTOM_NAV_KEYS = ["home", "checkIn", "plan", "learn", "companion"];

const syncLabel = (cloud) => {
  if (cloud.isOffline) return "Offline cache";
  if (cloud.syncStatus === "syncing") return "Saving…";
  if (cloud.lastSyncedAt) return "Saved to cloud";
  return "Cloud profile";
};

const CompanionAppShell = ({ assessment, install, children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const cloud = useCloudAccount();
  const [ownerAccess, setOwnerAccess] = useState(false);
  const activePage = getCompanionPageFromPath(location.pathname);
  const requestedInstall = new URLSearchParams(location.search).get("install") === "1";
  const preferredName = assessment?.identity?.preferredName;
  const bottomPages = COMPANION_APP_PAGES.filter((page) =>
    BOTTOM_NAV_KEYS.includes(page.key),
  );

  useEffect(() => {
    let cancelled = false;
    if (!cloud.account?.userId) {
      setOwnerAccess(false);
      return undefined;
    }
    getAdminSession()
      .then((session) => {
        if (!cancelled) setOwnerAccess(Boolean(session?.admin?.authorized));
      })
      .catch(() => {
        if (!cancelled) setOwnerAccess(false);
      });
    return () => {
      cancelled = true;
    };
  }, [cloud.account?.userId]);

  return (
    <div
      data-testid="companion-app-shell"
      className="min-h-screen forr-text-ink"
      style={{
        background:
          "linear-gradient(180deg, #F4F7F4 0%, #F8F6F0 42%, #F4F1E9 100%)",
      }}
    >
      <header
        className="sticky top-0 z-40 border-b backdrop-blur-xl"
        style={{
          backgroundColor: "rgba(248, 247, 242, 0.92)",
          borderColor: "var(--forr-line-soft)",
        }}
      >
        <div className="mx-auto flex min-h-[68px] max-w-[920px] items-center justify-between gap-4 px-4 sm:px-6">
          <button
            type="button"
            onClick={() => navigate("/app")}
            className="flex min-w-0 items-center gap-3 text-left"
            aria-label="Open Companion profile"
          >
            <div
              className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-[17px]"
              style={{
                background:
                  "linear-gradient(145deg, var(--forr-teal-deep), var(--forr-teal))",
                boxShadow: "0 10px 30px rgba(15,117,110,0.2)",
              }}
            >
              <Sparkles size={20} color="#fff" strokeWidth={1.8} />
            </div>
            <span className="min-w-0">
              <span className="block truncate font-display text-[17px] font-medium tracking-tight forr-text-ink">
                APOS Companion
              </span>
              <span className="mt-0.5 block truncate text-[10px] uppercase tracking-[0.19em] forr-text-mute">
                {preferredName
                  ? `${preferredName} · ${assessment?.profile?.title || "Adaptive Profile"}`
                  : assessment?.profile?.title || "Adaptive Physiology"}
              </span>
            </span>
          </button>

          <div className="flex items-center gap-2">
            {ownerAccess && (
              <button
                type="button"
                onClick={() => navigate("/admin")}
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full forr-surface"
                style={{ border: "1px solid var(--forr-line-soft)" }}
                aria-label="Open owner dashboard"
                title="Owner dashboard"
                data-testid="owner-dashboard-shortcut"
              >
                <ShieldCheck size={17} className="forr-text-teal-deep" />
              </button>
            )}

            <details className="relative">
              <summary
                className="flex h-10 w-10 cursor-pointer list-none items-center justify-center rounded-full forr-surface"
                style={{ border: "1px solid var(--forr-line-soft)" }}
                aria-label="Open cloud account status"
              >
                {cloud.isOffline ? (
                  <CloudOff size={17} className="text-amber-700" />
                ) : cloud.syncStatus === "syncing" ? (
                  <RefreshCw size={17} className="animate-spin forr-text-teal-deep" />
                ) : (
                  <Cloud size={17} className="forr-text-teal-deep" />
                )}
              </summary>
              <div
                className="absolute right-0 top-12 z-[70] w-[280px] rounded-2xl p-4 forr-surface"
                style={{
                  border: "1px solid var(--forr-line)",
                  boxShadow: "var(--forr-shadow-md)",
                }}
              >
                <div className="forr-overline">Account and storage</div>
                <div className="mt-2 break-all text-sm font-medium forr-text-ink">
                  {cloud.account?.email}
                </div>
                <div className="mt-3 rounded-xl p-3 forr-surface-tint">
                  <div className="text-xs font-medium forr-text-ink">{syncLabel(cloud)}</div>
                  <p className="mt-1 text-[11px] leading-relaxed forr-text-mute">
                    Assessments, check-ins, calendar, progress, and Companion history are cached here and synchronized to your private account.
                  </p>
                </div>
                {ownerAccess && (
                  <button
                    type="button"
                    onClick={() => navigate("/admin")}
                    className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full px-3 py-2.5 text-xs font-medium forr-btn-primary"
                  >
                    <ShieldCheck size={13} /> Owner dashboard
                  </button>
                )}
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={() => cloud.syncNow()}
                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-full px-3 py-2.5 text-xs forr-btn-secondary"
                  >
                    <RefreshCw size={13} /> Sync now
                  </button>
                  <button
                    type="button"
                    onClick={() => cloud.signOut()}
                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-full px-3 py-2.5 text-xs forr-btn-secondary"
                  >
                    <LogOut size={13} /> Sign out
                  </button>
                </div>
              </div>
            </details>

            <button
              type="button"
              onClick={install?.install}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full forr-surface"
              style={{ border: "1px solid var(--forr-line-soft)" }}
              aria-label={install?.installed ? "Companion installed" : "Install Companion"}
              title={install?.installed ? "Companion installed" : "Install Companion"}
            >
              <Download size={17} className="forr-text-teal-deep" />
            </button>
          </div>
        </div>
      </header>

      {requestedInstall && !install?.installed && (
        <div className="mx-auto max-w-[920px] px-4 pt-4 sm:px-6">
          <div
            className="rounded-[22px] p-5 forr-panel-action"
            style={{ border: "1px solid var(--forr-action-border)" }}
          >
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="forr-overline">Install APOS Companion</div>
                <h2 className="mt-2 font-display text-xl forr-text-ink">
                  Add Companion to this device
                </h2>
              </div>
              <button
                type="button"
                onClick={install?.install}
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-medium forr-btn-primary"
              >
                <Download size={16} /> Install
              </button>
            </div>
          </div>
        </div>
      )}

      {install?.manualHelp && !install?.installed && (
        <div className="mx-auto max-w-[920px] px-4 pt-4 sm:px-6">
          <div className="rounded-2xl p-4 forr-panel-information text-xs leading-relaxed forr-text-soft">
            On iPhone or iPad, open the browser Share menu and choose <strong>Add to Home Screen</strong>.
            On Android, choose <strong>Install app</strong> from the browser menu.
          </div>
        </div>
      )}

      <main
        className="mx-auto w-full max-w-[920px] px-4 py-5 sm:px-6 sm:py-7"
        style={{ paddingBottom: "calc(8.5rem + env(safe-area-inset-bottom))" }}
      >
        {children}
      </main>

      {activePage !== "companion" && (
        <button
          type="button"
          onClick={() => navigate("/app/companion")}
          className="fixed right-4 z-40 inline-flex items-center gap-2 rounded-full px-4 py-3 text-xs font-medium text-white shadow-xl sm:right-6"
          style={{
            bottom: "calc(5.7rem + env(safe-area-inset-bottom))",
            background:
              "linear-gradient(135deg, var(--forr-teal-deep), var(--forr-teal))",
          }}
        >
          <Sparkles size={15} /> Ask Companion
        </button>
      )}

      <nav
        data-testid="companion-bottom-navigation"
        className="fixed inset-x-0 bottom-0 z-50 border-t backdrop-blur-xl"
        style={{
          backgroundColor: "rgba(252, 251, 247, 0.96)",
          borderColor: "var(--forr-line)",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
        aria-label="Companion app navigation"
      >
        <div className="mx-auto grid max-w-[760px] grid-cols-5 px-1.5 sm:px-4">
          {bottomPages.map((page) => {
            const Icon = ICONS[page.key] || Home;
            const selected = activePage === page.key;
            return (
              <button
                type="button"
                key={page.key}
                onClick={() => navigate(page.path)}
                aria-current={selected ? "page" : undefined}
                className="relative flex min-h-[72px] flex-col items-center justify-center gap-1 px-1 text-center"
              >
                {selected && (
                  <span
                    className="absolute top-0 h-1 w-8 rounded-b-full"
                    style={{ backgroundColor: "var(--forr-teal)" }}
                  />
                )}
                <span
                  className="flex h-9 w-9 items-center justify-center rounded-2xl transition-colors"
                  style={{
                    backgroundColor: selected ? "var(--forr-teal-tint)" : "transparent",
                    color: selected ? "var(--forr-teal-deep)" : "var(--forr-ink-mute)",
                  }}
                >
                  <Icon size={19} strokeWidth={selected ? 2 : 1.7} />
                </span>
                <span
                  className="text-[10px] font-medium"
                  style={{
                    color: selected ? "var(--forr-teal-deep)" : "var(--forr-ink-mute)",
                  }}
                >
                  {page.shortLabel}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default CompanionAppShell;
